import { useMedplum, useMedplumProfile } from '@medplum/react-hooks';
import { useCallback, useEffect, useState } from 'react';
import type { Practitioner } from '@medplum/fhirtypes';
import { resolveCurrentUserPractitioner } from '../lib/practitionerIdentity';

/**
 * Resolves the current user to their Practitioner resource (for signing, saved signature, etc.).
 *
 * When the profile is a Practitioner, reads the resource from the server to
 * pick up extension changes (e.g. AI opt-out) that the MedplumClient's cached
 * profile may not reflect yet.
 */
export function useCurrentUserPractitioner(): {
  practitioner: Practitioner | null;
  loading: boolean;
  refetch: () => Promise<void>;
} {
  const medplum = useMedplum();
  const profile = useMedplumProfile();
  const [practitioner, setPractitioner] = useState<Practitioner | null>(null);
  const [loading, setLoading] = useState(true);

  const resolve = useCallback(async () => {
    if (profile?.resourceType === 'Practitioner' && profile.id) {
      try {
        const fresh = await medplum.readResource('Practitioner', profile.id, { cache: 'no-cache' });
        setPractitioner(fresh);
      } catch {
        setPractitioner(profile as Practitioner);
      }
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const pract = await resolveCurrentUserPractitioner(medplum, profile ?? undefined);
      setPractitioner(pract);
    } catch {
      setPractitioner(null);
    } finally {
      setLoading(false);
    }
  }, [medplum, profile]);

  useEffect(() => {
    resolve();
  }, [resolve]);

  return { practitioner, loading, refetch: resolve };
}
